/**
 * PlanAccordion Component
 * 
 * Displays an execution plan with collapsible phases/steps.
 * Part of the json-render component system.
 */

import { useState, useEffect } from 'react';
import {
    CardContent,
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
    Button,
} from '@pagespeedify/ui';
import {
    CheckCircle2,
    XCircle,
    Loader2,
    ChevronRight,
    MinusCircle,
    Ban,
    PlayCircle,
    Pencil,
    Trash2,
    Plus,
} from 'lucide-react';

import type { PlanAccordionProps, PlanAccordionPhase } from '../../types/json-render';

interface PlanAccordionComponentProps extends PlanAccordionProps {
    onApprove?: () => void;
    onCancel?: () => void;
    isApproving?: boolean;
    approvalTransitionInFlight?: boolean;
    isDormantPlan?: boolean;
    // Step modification callbacks (Phase 1 frontend)
    onEditStep?: (stepId: string, currentStep: PlanAccordionPhase) => void;
    onRemoveStep?: (stepId: string) => void;
    onAddStep?: (afterStepId?: string) => void;
    onStepFocusChange?: (step: PlanAccordionPhase | null) => void;
    isEditable?: boolean; // True when status is pending_approval or awaiting_user_input
}

function PhaseStatusIcon({ status }: { status: string }) {
    switch (status) {
        case 'completed':
            return <CheckCircle2 className="h-4 w-4 text-green-600" />;
        case 'failed':
            return <XCircle className="h-4 w-4 text-destructive" />;
        case 'blocked':
            return <Ban className="h-4 w-4 text-amber-500" />;
        case 'skipped':
            return <MinusCircle className="h-4 w-4 text-muted-foreground" />;
        case 'running':
        case 'in_progress':
            return <Loader2 className="h-4 w-4 animate-spin text-primary" />;
        default:
            return <ChevronRight className="h-4 w-4 text-muted-foreground" />;
    }
}

export default function PlanAccordion({
    planId: _planId,
    title,
    phases,
    status,
    showApprovalButton = false,
    onApprove,
    onCancel,
    isApproving = false,
    approvalTransitionInFlight = false,
    isDormantPlan = false,
    onEditStep,
    onRemoveStep,
    onAddStep,
    onStepFocusChange,
    isEditable = false,
}: PlanAccordionComponentProps) {
    const completedCount = phases.filter((p) => p.status === 'completed').length;
    const failedCount = phases.filter((p) => p.status === 'failed').length;
    const skippedCount = phases.filter((p) => p.status === 'skipped' || p.status === 'blocked').length;
    const totalCount = phases.length;

    // Latch approval state to prevent button flickering
    const [wasApproved, setWasApproved] = useState(false);
    useEffect(() => {
        if (isApproving) {
            setWasApproved(true);
        }
    }, [isApproving]);

    // Derived state for showing approved UI
    const showApprovedUI = isApproving || wasApproved || approvalTransitionInFlight || status === 'executing';

    const [isPlanExpanded, setIsPlanExpanded] = useState(!isDormantPlan);

    return (
        <div className="bg-muted/30 overflow-hidden">
            <CardContent className="p-4">
                {/* Header */}
                <button
                    type="button"
                    onClick={() => setIsPlanExpanded((prev) => !prev)}
                    className="flex w-full items-center justify-between mb-3 text-left"
                >
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{title}</span>
                        <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded">
                            {completedCount} done
                            {failedCount > 0 ? ` | ${failedCount} failed` : ''}
                            {skippedCount > 0 ? ` | ${skippedCount} skipped/blocked` : ''}
                            {` / ${totalCount}`}
                        </span>
                        {isDormantPlan && (
                            <span className="text-xs bg-muted text-muted-foreground px-1.5 py-0.5 rounded">
                                Dormant plan version
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        {status === 'executing' && (
                            <Loader2 className="h-4 w-4 animate-spin text-primary" />
                        )}
                        {status === 'completed' && (
                            <CheckCircle2 className="h-4 w-4 text-green-600" />
                        )}
                        {isPlanExpanded ? (
                            <MinusCircle className="h-4 w-4 text-muted-foreground" />
                        ) : (
                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        )}
                    </div>
                </button>

                {/* Phases/Steps */}
                {isPlanExpanded && (
                    <Accordion type="multiple" className="space-y-1">
                        {phases.map((phase) => (
                            <AccordionItem
                                key={phase.id}
                                value={phase.id}
                                className="bg-background/50 border-0"
                            >
                                <AccordionTrigger
                                    className="px-3 py-2 hover:no-underline"
                                    onClick={() => onStepFocusChange?.(phase)}
                                >
                                    <div className="flex items-center gap-2 text-left">
                                        <PhaseStatusIcon status={phase.status} />
                                        <span className="text-xs font-medium">
                                            {phase.order}. {phase.title}
                                        </span>
                                    </div>
                                </AccordionTrigger>
                                <AccordionContent className="px-3 pb-3">
                                    {phase.description && (
                                        <p className="text-xs text-muted-foreground mb-2">
                                            {phase.description}
                                        </p>
                                    )}
                                    {phase.statusReason && (
                                        <p className="text-xs text-muted-foreground mb-2">
                                            Reason: {phase.statusReason}
                                        </p>
                                    )}
                                    {/* Step modification buttons - only show when editable */}
                                    {isEditable && phase.status !== 'completed' && phase.status !== 'in_progress' && (
                                        <div className="flex items-center gap-1 mt-2 pt-2 border-t border-border/50">
                                            {onEditStep && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onStepFocusChange?.(phase);
                                                        onEditStep(phase.id, phase);
                                                    }}
                                                    className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                                                >
                                                    <Pencil className="h-3 w-3 mr-1" />
                                                    Edit
                                                </Button>
                                            )}
                                            {onRemoveStep && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onStepFocusChange?.(phase);
                                                        onRemoveStep(phase.id);
                                                    }}
                                                    className="h-7 px-2 text-xs text-destructive/70 hover:text-destructive"
                                                >
                                                    <Trash2 className="h-3 w-3 mr-1" />
                                                    Remove
                                                </Button>
                                            )}
                                        </div>
                                    )}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                )}

                {/* Add Step Button - only show when editable */}
                {isPlanExpanded && isEditable && onAddStep && (
                    <div className="mt-2 flex justify-center">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onAddStep()}
                            className="h-7 px-3 text-xs text-muted-foreground hover:text-foreground"
                        >
                            <Plus className="h-3 w-3 mr-1" />
                            Add Step
                        </Button>
                    </div>
                )}

                {/* Approval Section */}
                <div className="mt-4 flex justify-center gap-2">
                    {showApprovedUI ? (
                        <div className="flex items-center gap-2 text-green-600 font-medium py-2">
                            <CheckCircle2 className="h-5 w-5" />
                            <span>Plan Approved</span>
                            <Loader2 className="h-4 w-4 animate-spin ml-1" />
                        </div>
                    ) : showApprovalButton && !isDormantPlan && onApprove && !approvalTransitionInFlight ? (
                        <>
                            {onCancel && (
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={onCancel}
                                    className="h-9 w-9 text-muted-foreground hover:text-foreground"
                                    title="Cancel Plan"
                                >
                                    <XCircle className="h-5 w-5" />
                                </Button>
                            )}
                            <Button
                                onClick={onApprove}
                                disabled={isApproving}
                                className="gap-2"
                            >
                                {isApproving ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        Approving...
                                    </>
                                ) : (
                                    <>
                                        <PlayCircle className="h-4 w-4" />
                                        Approve Plan
                                    </>
                                )}
                            </Button>
                        </>
                    ) : null}
                </div>
            </CardContent>
        </div>
    );
}
